<template>
  <CCard>
    <CCardHeader class="d-flex justify-content-end align-items-center">
      <h2 class="text-uppercase font-weight-bold mb-0 mr-auto">
        Enterprise WASPs
      </h2>

      <CInput
        v-model="filters.search"
        class="mb-0"
        placeholder="Search MAC or Name"
        @update:value="fetchData"
        @focusout.stop
      />
    </CCardHeader>
    <CCardBody>
      <CDataTable
        :items="data"
        :fields="fields"
        :pagination="false"
        :sorter="{ external: true }"
        :loading="loading"
        class="mb-0"
        @update:sorter-value="sortingChange"
      >
        <template #enterprise_user="{ item }">
          <td>
            <div v-if="item.enterprise_user">
              <strong>{{ item.enterprise_user.name }}</strong>
              <br>
              <small class="text-muted">{{ item.enterprise_user.email }}</small>
            </div>
            <span
              v-else
              class="text-muted font-italic"
            >Unassigned</span>
          </td>
        </template>

        <template #alias="{ item }">
          <td>
            <div class="d-flex align-items-center">
              <span>{{ item.alias || '—' }}</span>
              <CButton
                size="sm"
                color="link"
                class="ml-2 p-0"
                v-c-tooltip="'Edit Alias'"
                @click="openAliasModal(item)"
              >
                <i class="fas fa-pen" />
              </CButton>
            </div>
          </td>
        </template>

        <template #active="{ item }">
          <td class="text-center">
            <span :class="item.active ? 'badge badge-success' : 'badge badge-secondary'">
              {{ item.active ? 'Active' : 'Inactive' }}
            </span>
          </td>
        </template>

        <template #actions="{ item }">
          <td>
            <div class="d-flex align-items-center">
              <Spinner
                v-if="loadingWasps[item.id]"
                :small="true"
                class="mr-2"
              />
              <template v-else>
                <CButton
                  v-if="!item.active"
                  v-c-tooltip="'Activate WASP'"
                  size="sm"
                  color="success"
                  class="text-white mr-2"
                  @click="activateWasp(item.id)"
                >
                  <i class="fas fa-play" />
                </CButton>

                <CButton
                  v-if="item.active"
                  v-c-tooltip="'Deactivate WASP'"
                  size="sm"
                  color="secondary"
                  class="text-white mr-2"
                  @click="deactivateWasp(item.id)"
                >
                  <i class="fas fa-pause" />
                </CButton>

                <CButton
                  v-c-tooltip="item.enterprise_user ? 'Transfer to another User' : 'Assign to User'"
                  size="sm"
                  color="info"
                  class="text-white mr-2"
                  @click="openUserSelectModal(item)"
                >
                  <i class="fas fa-exchange-alt" />
                </CButton>

                <CButton
                  v-if="item.enterprise_user"
                  v-c-tooltip="'Unassign User'"
                  size="sm"
                  color="warning"
                  class="text-white mr-2"
                  @click="unassignWasp(item.id)"
                >
                  <i class="fas fa-user-times" />
                </CButton>
              </template>
            </div>
          </td>
        </template>
      </CDataTable>

      <pagination
        :current-page="options.page"
        :last-page="options.last_page"
        class="mr-3"
        @paginate="pageChange"
      />
    </CCardBody>

    <EnterpriseUserSelectModal
      ref="userSelectModal"
      @selected="handleUserSelected"
    />

    <CModal
      :visible="aliasModalVisible"
      @update:visible="aliasModalVisible = $event"
      size="sm"
      centered
    >
      <template #header-wrapper>
        <CCardHeader class="d-flex justify-content-between align-items-center">
          <h6 class="card-title mb-0">Edit Alias</h6>
        </CCardHeader>
      </template>
      <CCardBody>
        <CInput
          v-model="aliasForm.alias"
          label="Alias"
          placeholder="Enter alias"
          @keyup.enter="saveAlias"
        />
      </CCardBody>
      <template #footer-wrapper>
        <CCardFooter class="d-flex justify-content-end">
          <CButton size="sm" color="secondary" class="mr-2" @click="aliasModalVisible = false">
            Cancel
          </CButton>
          <CButton size="sm" color="primary" :disabled="!aliasForm.alias" @click="saveAlias">
            Save
          </CButton>
        </CCardFooter>
      </template>
    </CModal>
  </CCard>
</template>

<script>
import externalTable from '@/mixins/externalTable';
import EnterpriseUserSelectModal from '@/components/EnterpriseUserSelectModal.vue';
import Spinner from '@/components/Spinner.vue';

export default {
  name: 'WaspsList',
  components: {
    EnterpriseUserSelectModal,
    Spinner,
  },
  mixins: [externalTable],
  data() {
    return {
      filters: {
        search: '',
      },
      fields: [
        { key: 'mac', label: 'MAC Address' },
        { key: 'name', label: 'Name' },
        { key: 'alias', label: 'Alias' },
        { key: 'enterprise_user', label: 'Assigned User', sorter: false },
        { key: 'active', label: 'Status' },
        { key: 'actions', label: 'Actions', sorter: false },
      ],
      url: '/wasps',
      activeWaspForAssignment: null,
      loadingWasps: {},
      aliasModalVisible: false,
      aliasForm: { id: null, alias: '' },
    };
  },
  mounted() {
    this.fetchData();
  },
  beforeUnmount() {
    this.hideTooltips();
  },
  methods: {
    hideTooltips() {
      document.querySelectorAll('.tooltip.bs-tooltip-auto, .tooltip.bs-tooltip-top, .tooltip.bs-tooltip-bottom, .tooltip.bs-tooltip-start, .tooltip.bs-tooltip-end').forEach((el) => el.remove());
    },
    activateWasp(id) {
      this.hideTooltips();
      this.addLoadingWasp(id);
      this.$http
        .post(`${this.url}/${id}/activate`)
        .then(({ data }) => {
          this.$toast.success(data.message || 'WASP activated!');
          this.fetchData();
        })
        .catch(({ response }) => {
          this.$toast.error(response?.data?.message || 'Error activating WASP');
        })
        .finally(() => {
          this.removeLoadingWasp(id);
        });
    },

    deactivateWasp(id) {
      this.hideTooltips();
      this.addLoadingWasp(id);
      this.$http
        .post(`${this.url}/${id}/deactivate`)
        .then(({ data }) => {
          this.$toast.success(data.message || 'WASP deactivated!');
          this.fetchData();
        })
        .catch(({ response }) => {
          this.$toast.error(response?.data?.message || 'Error deactivating WASP');
        })
        .finally(() => {
          this.removeLoadingWasp(id);
        });
    },

    openUserSelectModal(wasp) {
      this.hideTooltips();
      this.activeWaspForAssignment = wasp;
      this.$refs.userSelectModal.open();
    },

    handleUserSelected(user) {
      if (!this.activeWaspForAssignment) return;

      const waspId = this.activeWaspForAssignment.id;
      const userId = user.id;

      this.addLoadingWasp(waspId);

      this.$http
        .post(`${this.url}/${waspId}/assign`, { enterprise_user_id: userId })
        .then(({ data }) => {
          this.$toast.success(data.message || 'WASP assigned successfully!');
          this.fetchData();
        })
        .catch(({ response }) => {
          this.$toast.error(response?.data?.message || 'Error assigning WASP');
        })
        .finally(() => {
          this.activeWaspForAssignment = null;
          this.removeLoadingWasp(waspId);
        });
    },

    unassignWasp(id) {
      this.hideTooltips();
      if (!confirm('Are you sure you want to unassign this WASP from its user?')) return;

      this.addLoadingWasp(id);
      this.$http
        .post(`${this.url}/${id}/unassign`)
        .then(({ data }) => {
          this.$toast.success(data.message || 'WASP unassigned successfully!');
          this.fetchData();
        })
        .catch(({ response }) => {
          this.$toast.error(response?.data?.message || 'Error unassigning WASP');
        })
        .finally(() => {
          this.removeLoadingWasp(id);
        });
    },

    addLoadingWasp(id) {
      this.loadingWasps[id] = true;
    },

    removeLoadingWasp(id) {
      delete this.loadingWasps[id];
    },

    openAliasModal(wasp) {
      this.hideTooltips();
      this.aliasForm = { id: wasp.id, alias: wasp.alias || '' };
      this.aliasModalVisible = true;
    },

    saveAlias() {
      const { id, alias } = this.aliasForm;
      this.addLoadingWasp(id);
      this.$http
        .post(`${this.url}/${id}/alias`, { alias })
        .then(({ data }) => {
          this.$toast.success(data.message || 'Alias updated!');
          this.aliasModalVisible = false;
          this.fetchData();
        })
        .catch(({ response }) => {
          this.$toast.error(response?.data?.message || 'Error updating alias');
        })
        .finally(() => {
          this.removeLoadingWasp(id);
        });
    },
  },
};
</script>
